import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { Order, TimeSlot, TransportType } from '@klareospace/pharos-lib-types';
import { TRANSPORT_TYPE } from '@klareospace/pharos-lib-types';
import { isInvertedTimeSlot } from '@klareospace/pharos-lib-utils';
import { toDateTimeRangeValue, toTimeSlot } from '@klareospace/pharos-lib-web-utils';
import type { DateTimeRangeValue } from '@klareospace/pharos-lib-web-utils';

import type { UpdateOrderRequest } from '../api';
import { useOrderUpdateErrorMessage } from './use-order-update-error-message';
import { useUpdateOrder } from './use-update-order';

// ─── Types ────────────────────────────────────────────────────────────────────

/** `pickupTimeSlot`/`deliveryTimeSlot` patch the non-matching leg directly; the matching leg always uses `requestedTimeWindow`. */
export type TimeWindowEditTarget = 'deliveryTimeSlot' | 'pickupTimeSlot' | 'requestedTimeWindow';

export interface UseTimeWindowEditOptions {
  onSuccess: () => void;
  order: Order;
  target: TimeWindowEditTarget;
}

export interface UseTimeWindowEditReturn {
  errorMessage: string | null;
  isPending: boolean;
  isSaveDisabled: boolean;
  onChange: (value: DateTimeRangeValue) => void;
  onSubmit: () => void;
  value: DateTimeRangeValue;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveInitialTimeSlot(order: Order, target: TimeWindowEditTarget): TimeSlot | undefined {
  if (target === 'pickupTimeSlot') return order.pickup.timeSlot;
  if (target === 'deliveryTimeSlot') return order.delivery.timeSlot;
  return order.requestedTimeWindow;
}

/** `requestedTimeWindow` always edits the leg matching `order.transportType` (see resolveTimeslotTarget). */
function resolveEditedLeg(order: Order, target: TimeWindowEditTarget): TransportType {
  if (target === 'pickupTimeSlot') return TRANSPORT_TYPE.PICKUP;
  if (target === 'deliveryTimeSlot') return TRANSPORT_TYPE.DELIVERY;
  return order.transportType;
}

/** True when the delivery leg would end before the pickup leg starts — only checked when both legs have a slot. */
function hasCrossLegConflict(pickupSlot: TimeSlot | undefined, deliverySlot: TimeSlot | undefined): boolean {
  if (!pickupSlot || !deliverySlot) return false;
  return new Date(deliverySlot.end).getTime() < new Date(pickupSlot.start).getTime();
}

function resolveValidationKey(hasInvertedRange: boolean, hasCrossLegViolation: boolean): string | null {
  if (hasInvertedRange) return 'drawer.edit.timeWindow.invalidRange';
  if (hasCrossLegViolation) return 'drawer.edit.timeWindow.crossLegInvalid';
  return null;
}

function buildUpdatePatch(target: TimeWindowEditTarget, timeSlot: TimeSlot): UpdateOrderRequest {
  if (target === 'pickupTimeSlot') return { pickup: { timeSlot } };
  if (target === 'deliveryTimeSlot') return { delivery: { timeSlot } };
  return { requestedTimeWindow: timeSlot };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Owns the timewindow pop-in's state for all three editable timeslot rows (see `TimeWindowEditTarget`). */
export function useTimeWindowEdit({ onSuccess, order, target }: UseTimeWindowEditOptions): UseTimeWindowEditReturn {
  const { t } = useTranslation('orders');
  const updateOrder = useUpdateOrder(order.id);
  const resolveErrorMessage = useOrderUpdateErrorMessage(order.id);

  const [value, setValue] = useState<DateTimeRangeValue>(() =>
    toDateTimeRangeValue(resolveInitialTimeSlot(order, target)),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const timeSlot = toTimeSlot(value);
  const hasInvertedRange = isInvertedTimeSlot(timeSlot);

  const editedLeg = resolveEditedLeg(order, target);
  const effectivePickupSlot = editedLeg === TRANSPORT_TYPE.PICKUP ? timeSlot : order.pickup.timeSlot;
  const effectiveDeliverySlot = editedLeg === TRANSPORT_TYPE.DELIVERY ? timeSlot : order.delivery.timeSlot;
  const hasCrossLegViolation = hasCrossLegConflict(effectivePickupSlot, effectiveDeliverySlot);

  const validationKey = resolveValidationKey(hasInvertedRange, hasCrossLegViolation);
  const displayedError = validationKey ? t(validationKey) : errorMessage;

  function onSubmit(): void {
    if (hasInvertedRange || hasCrossLegViolation) return;

    setErrorMessage(null);
    updateOrder.mutate(buildUpdatePatch(target, timeSlot), {
      onError: (error: unknown) => {
        setErrorMessage(resolveErrorMessage(error));
      },
      onSuccess: () => {
        toast.success(t('drawer.edit.timeWindow.success'));
        onSuccess();
      },
    });
  }

  return {
    errorMessage: displayedError,
    isPending: updateOrder.isPending,
    isSaveDisabled: updateOrder.isPending || hasInvertedRange || hasCrossLegViolation,
    onChange: setValue,
    onSubmit,
    value,
  };
}
